import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { global } from "@shared/libs/Global";
import { showDialog } from "@widgets/CommonDialog";
import { UseApiService } from "@shared/hooks/useApiService";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import IconButton from "@mui/material/IconButton";

interface ChangePwdPopupProps {
  modalRef?: any;
}

const ChangePwdPopup = forwardRef(({ modalRef }: ChangePwdPopupProps, ref) => {
  const { userInfo, config } = global();
  const { apiPost } = UseApiService();

  const currentPwdRef = useRef<HTMLInputElement>(null);
  const newPwdRef = useRef<HTMLInputElement>(null);
  const confirmRef = useRef<HTMLInputElement>(null);

  const [currentPwd, setPassword] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showCurrentPwd, setShowCurrentPwd] = useState(false);
  const [showNewPwd, setShowNewPwd] = useState(false);
  const [showConfirmPwd, setShowConfirmPwd] = useState(false);

  useEffect(() => {
    currentPwdRef.current?.focus();
  }, []);

  useImperativeHandle(ref, () => ({
    getSelectedValue: async () => {
      if (!currentPwd || !newPwd || !confirm) {
        return { code: "POPUP_CHANGEPWD", data: "empty" };
      }

      if (newPwd !== confirm) {
        return { code: "POPUP_CHANGEPWD", data: "notConfirm" };
      }

      const policyError = validatePasswordPolicy(newPwd);
      if (policyError) {
        await showDialog("alter", policyError);
        return { code: "POPUP_CHANGEPWD", data: "policyFail" };
      }

      const isSaved = await changePwd();
      if (isSaved.code == 200) {
        await showDialog("success", "비밀번호가 변경되었습니다.");
        return { code: "POPUP_CHANGEPWD", data: "" };
      } else {
        return { code: "POPUP_CHANGEPWD", data: "keepOpened" };
      }
    },
    focusToCurrentPwdInput: () => {
      currentPwdRef.current?.focus();
    },
    focusToNewPwdInput: () => {
      newPwdRef.current?.focus();
    },
  }));

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    current: "currentPwd" | "newPwd" | "confirm"
  ) => {
    if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();

      if (current === "currentPwd") {
        newPwdRef.current?.focus();
      } else if (current === "newPwd") {
        confirmRef.current?.focus();
      } else {
        modalRef?.current?.handleConfirm(true);
      }
    }
  };

  const changePwd = async () => {
    return await apiPost("/user/change-password", {
      userId: userInfo.cd_user,
      currentPassword: currentPwd,
      newPassword: newPwd,
      confirmPassword: confirm,
    });
  };

  const validatePasswordPolicy = (password: string) => {
    const minLength = Number(config.SYS2012.dc_ctrl ?? 0);
    const numberCount = Number(config.SYS2013.dc_ctrl ?? 0);
    const specialCount = Number(config.SYS2014.dc_ctrl ?? 0);
    const alphaCount = Number(config.SYS2015.dc_ctrl ?? 0);
    const upperCount = Number(config.SYS2016.dc_ctrl ?? 0);

    const numbers = (password.match(/[0-9]/g) || []).length;
    const specials = (
      password.match(/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/g) || []
    ).length;
    const alphabets = (password.match(/[a-zA-Z]/g) || []).length;
    const uppers = (password.match(/[A-Z]/g) || []).length;

    if (minLength > 0 && password.length < minLength) {
      return `비밀번호는 ${minLength}자 이상이어야 합니다.`;
    }

    if (numberCount > 0 && numbers < numberCount) {
      return `비밀번호에 숫자를 ${numberCount}개 이상 포함해야 합니다.`;
    }

    if (specialCount > 0 && specials < specialCount) {
      return `비밀번호에 특수문자를 ${specialCount}개 이상 포함해야 합니다.`;
    }

    if (alphaCount > 0 && alphabets < alphaCount) {
      return `비밀번호에 영문자를 ${alphaCount}개 이상 포함해야 합니다.`;
    }

    if (upperCount > 0 && uppers < upperCount) {
      return `비밀번호에 대문자를 ${upperCount}개 이상 포함해야 합니다.`;
    }

    return "";
  };

  return (
    <div className="w-full">
      <div className="mb-3">
        <label className="block text-sm mb-1">현재 비밀번호</label>
        <div style={{ position: "relative" }}>
          <input
            ref={currentPwdRef}
            type={showCurrentPwd ? "text" : "password"}
            value={currentPwd}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => handleKeyDown(e, "currentPwd")}
            className="w-full border px-2 py-1 pr-10 rounded"
            style={{ borderColor: "rgb(196 196 196)" }}
          />

          <IconButton
            size="small"
            onClick={() => setShowCurrentPwd(!showCurrentPwd)}
            sx={{
              position: "absolute",
              right: 8,
              top: "50%",
              transform: "translateY(-50%)",
            }}
          >
            {showCurrentPwd ? (
              <VisibilityOff fontSize="small" />
            ) : (
              <Visibility fontSize="small" />
            )}
          </IconButton>
        </div>
      </div>

      <div className="mb-3">
        <label className="block text-sm mb-1">새 비밀번호</label>
        <div style={{ position: "relative" }}>
          <input
            ref={newPwdRef}
            type={showNewPwd ? "text" : "password"}
            value={newPwd}
            onChange={(e) => setNewPwd(e.target.value)}
            onKeyDown={(e) => handleKeyDown(e, "newPwd")}
            className="w-full border px-2 py-1 pr-10 rounded"
            style={{ borderColor: "rgb(196 196 196)" }}
          />

          <IconButton
            size="small"
            onClick={() => setShowNewPwd((prev) => !prev)}
            sx={{
              position: "absolute",
              right: 8,
              top: "50%",
              transform: "translateY(-50%)",
            }}
          >
            {showNewPwd ? (
              <VisibilityOff fontSize="small" />
            ) : (
              <Visibility fontSize="small" />
            )}
          </IconButton>
        </div>
      </div>

      <div className="mb-3">
        <label className="block text-sm mb-1">새 비밀번호 확인</label>
        <div style={{ position: "relative" }}>
          <input
            ref={confirmRef}
            type={showConfirmPwd ? "text" : "password"}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            onKeyDown={(e) => handleKeyDown(e, "confirm")}
            onPaste={(e) => e.preventDefault()}
            className="w-full border px-2 py-1 pr-10 rounded"
            style={{ borderColor: "rgb(196 196 196)" }}
          />

          <IconButton
            size="small"
            onClick={() => setShowConfirmPwd((prev) => !prev)}
            sx={{
              position: "absolute",
              right: 8,
              top: "50%",
              transform: "translateY(-50%)",
            }}
          >
            {showConfirmPwd ? (
              <VisibilityOff fontSize="small" />
            ) : (
              <Visibility fontSize="small" />
            )}
          </IconButton>
        </div>
      </div>
    </div>
  );
});

export default ChangePwdPopup;
